import React, { useState } from "react";
import { Box, Typography, Divider, Chip, useTheme } from "@mui/material";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import FlexBetween from "components/FlexBetween";


const safeFormat = (date, pattern = "ddMMMyy") => {
    if (!date) return "";
    const d = new Date(date);
    return isNaN(d.getTime()) ? "" : formatInTimeZone(d, "UTC", pattern);
};


const SectorRow = React.memo(({ 
    timeMode, 
    theme, 
    index, 
    routeId, 
    flight, 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    
    const flightKey = flight._id || `${routeId}-${index}`;
    const fullAtd = timeMode === "UTC"
        ? safeFormat(flight.fullAtd || flight.fullEtd)
        : safeFormat(flight.fullAtdLocalBase || flight.fullEtdLocalBase);

    const atd = timeMode === "UTC" ? (flight.atd || flight.etd) : (flight.atdLocalBase || flight.etdLocalBase);
    const ata = timeMode === "UTC" ? (flight.ata || flight.eta) : (flight.ataLocalBase || flight.etaLocalBase);
    
    const crewMembers = React.useMemo(() => {
        return Array.from(
            new Map(
                (flight.crewMembers || [])
                    .sort((a, b) => a.rankPriority - b.rankPriority)
                    .map(c => [c.crewCode, c])
            ).values()
        );
    }, [flight.crewMembers]);

    
    return (
        <Box key={flightKey} display="flex" flexDirection="column" my={0.5} gap={0.5}>
            <FlexBetween
                sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <Typography width={20} align="center">{isOpen ? "▾" : "▸"}</Typography>
                <Typography width={40} variant="body2">{flight.flightNo}</Typography>
                <Typography width={55} variant="body2" align="center">{flight.flightReg || flight.acType}</Typography>
                <Typography width={50} variant="body2">{fullAtd}</Typography>
                <Typography width={35} variant="body2" align="center">{atd}</Typography>
                <Typography width={30} variant="body2" align="center">{flight.dep}</Typography>
                <Typography width={30} variant="body2" align="center">{flight.arr}</Typography>
                <Typography width={35} variant="body2" align="center">{ata}</Typography>
            </FlexBetween>
            
            <Divider />
            
            {isOpen && (
                <Box sx={{ ml: 2 }}>
                    {crewMembers.map((member, idx) => (
                        <Box
                            key={member.crewCode}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{
                                color: member.gender === "FM"
                                    ? theme.palette.secondary.main
                                    : theme.palette.primary.dark,
                            }}
                        >
                            <Typography width={20} align="right">{idx + 1}.</Typography>
                            <Typography width={36} variant="body2" align="right">{member.crewCode}</Typography>
                            <Typography variant="body2">{member.crewName} ({member.post} | {member.base})</Typography>
                        </Box>
                    ))}
                    <Divider sx={{ mt: 0.5 }} />
                </Box>
            )}
        </Box>
    );
});


const RouteItem = React.memo(({ route, idx, timeMode }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                p: 1,
                border: `1px solid ${theme.palette.neutral?.deep || "#ddd"}`,
                borderRadius: 1,
                bgcolor: "background.paper",
                boxShadow: 1,
            }}
        >
            <FlexBetween>
                <Box display="flex" alignItems="center" gap={1}>
                    <Chip label={idx + 1} size="small" variant="outlined" sx={{ height: 20 }} />
                    <Typography variant="subtitle2">
                        {route.routeDate ? format(new Date(route.routeDate), "dd/MM/yy") : "N/A"}
                    </Typography>
                    <Typography variant="subtitle2">{route.routeNo || ""}</Typography>
                </Box>
            </FlexBetween>

            <Divider sx={{ mt: 1 }} />

            <Box>
                {route.sectors?.map((flight, index) => (
                    <SectorRow 
                        key={flight._id || index}
                        timeMode={timeMode}
                        theme={theme}
                        index={index}
                        routeId={route._id}
                        flight={flight}
                    />
                ))}
            </Box>
        </Box>
    );
});

export default RouteItem;